import type { ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@mlm/ui';
import { useHasPerm } from '../lib/auth-store';

/**
 * Route guard for cashier pages that are a single authority (today: Consign
 * Out). Mirrors the admin SPA's guard — nav hiding alone is not enough, because
 * a page is still reachable by typing its URL, and the server 403 that follows
 * reads as a bug rather than as a permission boundary.
 *
 * super_admin always passes (handled inside `useHasPerm`). Permissions are
 * baked into the access token at login, so a perm granted in the admin Access
 * page only reaches the till on the next sign-in / token refresh.
 */
export function RequirePerm({
  perm,
  label,
  children,
}: {
  perm: string;
  /** Friendly label for the "no access" message. */
  label: string;
  children: ReactNode;
}) {
  const allowed = useHasPerm(perm);
  if (allowed) return <>{children}</>;
  return (
    <Card>
      <CardHeader>
        <CardTitle>No access</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          Your role doesn't include the{' '}
          <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">{perm}</code> permission,
          so <strong>{label}</strong> is hidden. Ask an admin to grant it (admin app: Members →
          Access), then sign out and back in.
        </p>
      </CardContent>
    </Card>
  );
}
